import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight, Loader2, Zap, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { STATI, statoInfo, TIPI, fmtDate } from "./praticheConst";
import { PraticaForm } from "./PraticaForm";
import { PraticaFiles } from "./PraticaFiles";

const Progress = ({ stato }) => {
  const idx = STATI.findIndex((s) => s[0] === stato);
  return (
    <div className="flex gap-0.5" title={`${idx + 1} / ${STATI.length}`}>
      {STATI.map(([k, l], i) => <div key={k} title={l} className="h-1.5 flex-1 rounded-full" style={{ background: i <= idx ? statoInfo(stato)[2] : "#E2E8F0" }} />)}
    </div>
  );
};

const Info = ({ label, value, testid }) => (
  <div><div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</div><div className="text-sm font-medium truncate" data-testid={testid}>{value || "—"}</div></div>
);

const PraticaCard = ({ p, canEdit, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [, label, color] = statoInfo(p.stato);
  return (
    <div className="card-shell p-4 space-y-3" data-testid={`pratica-${p.id}`}>
      <div className="flex items-start gap-2">
        <button onClick={() => setOpen(!open)} className="flex-1 text-left min-w-0" data-testid={`pratica-toggle-${p.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <span className="font-bold">{p.codice_pratica || "Pratica senza codice"}</span>
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full text-white" style={{ background: color }} data-testid={`pratica-stato-${p.id}`}>{label}</span>
            <span className="text-[11px] px-2 py-0.5 rounded-full border" style={{ borderColor: "var(--border)" }}>{TIPI[p.tipo_pratica]}</span>
          </div>
          <div className="text-xs mt-1 truncate" style={{ color: "var(--text-muted)" }}>{p.soggetto_titolare || "—"} · POD {p.pod || "—"}</div>
        </button>
        {canEdit && <>
          <button onClick={() => onEdit(p)} className="p-1.5 rounded hover:bg-black/5" data-testid={`pratica-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
          <button onClick={() => onDelete(p)} className="p-1.5 rounded hover:bg-black/5" style={{ color: "#B30000" }} data-testid={`pratica-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
        </>}
      </div>
      <Progress stato={p.stato} />
      {open && (
        <div className="space-y-4 pt-1">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Info label="Data assegnazione" value={p.data_assegnazione && fmtDate(p.data_assegnazione)} />
            <Info label="Data inoltro" value={p.data_inoltro && fmtDate(p.data_inoltro)} />
            <Info label="Commessa" value={p.commessa_name} />
            <Info label="Portale distributore" value={p.portale_distributore} />
            {p.tipo_pratica === "ordinaria" && <Info label="Gaudì Terna" testid={`pratica-gaudi-${p.id}`} value={p.gaudi_aperta ? `Aperta${p.codice_gaudi ? ` · ${p.codice_gaudi}` : ""}` : "Da aprire"} />}
          </div>
          {p.note && <div className="text-sm whitespace-pre-wrap">{p.note}</div>}
          <PraticaFiles pid={p.id} canEdit={canEdit} />
          {(p.stato_history || []).length > 0 && (
            <div className="text-xs space-y-1" data-testid={`pratica-history-${p.id}`}>
              <div className="font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Storico stati</div>
              {[...p.stato_history].reverse().map((h, i) => <div key={i} className="flex gap-2"><CheckCircle2 className="w-3.5 h-3.5" style={{ color: statoInfo(h.stato)[2] }} /> {statoInfo(h.stato)[1]} · {new Date(h.at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })} {h.by && `· ${h.by}`}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function PraticheAllaccio({ cid, cliente, canEdit }) {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/clienti/${cid}/pratiche-allaccio`).then(({ data }) => setList(data || [])).catch((e) => { toast.error(formatApiError(e)); setList([]); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  const remove = async (p) => {
    if (!window.confirm(`Eliminare la pratica ${p.codice_pratica || ""}?`)) return;
    try { await api.delete(`/pratiche-allaccio/${p.id}`); toast.success("Pratica eliminata"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="pratiche-allaccio">
      <div className="flex items-center gap-2">
        <Zap className="w-5 h-5" style={{ color: "var(--primary)" }} />
        <div className="font-bold flex-1">Pratiche allaccio in rete</div>
        {canEdit && <button onClick={() => setEditing({})} className="btn-primary" data-testid="pratica-add"><Plus className="w-4 h-4" /> Nuova pratica</button>}
      </div>
      {list === null ? <Loader2 className="w-5 h-5 animate-spin" /> : list.length === 0 ? (
        <div className="card-shell p-6 text-sm text-center" style={{ color: "var(--text-muted)" }} data-testid="pratiche-empty">Nessuna pratica di allaccio per questo cliente.</div>
      ) : list.map((p) => <PraticaCard key={p.id} p={p} canEdit={canEdit} onEdit={setEditing} onDelete={remove} />)}
      {editing && <PraticaForm cid={cid} cliente={cliente} pratica={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
